import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";

import { Header } from "../../../../components/Header/Header";
import api from "../../../chat/api";
import ChatWithProposal from "./ChatWithProposal";
import AIAnalysis from "./AIAnalysis";
import ProposalEditor from "./ProposalEditor";

import {
  convertProposalContentToHTML,
  parseHTMLToStructuredContent,
  parseListString,
} from "./proposalContentParser";

const ReviewerView = React.memo(({ proposalContent }) => (
  <div className="flex-1 overflow-y-auto p-8 custom-scrollbar bg-white min-h-0">
    <div className="max-w-4xl mx-auto">
      {proposalContent?.map((section, index) => (
        <div key={index} className="mb-8">
          <h2 className="text-[#0369A1] font-outfit text-xl font-semibold mb-3">
            {section.number}. {section.title}
          </h2>
          {section.response?.trim() && (
            <div className="text-[#374151] font-outfit text-[15px] leading-relaxed mb-4 whitespace-pre-wrap">
              {section.response}
            </div>
          )}
          {section.children?.map((child, childIndex) => (
            <div key={childIndex} className="ml-0 mb-5 mt-4">
              <h3 className="text-[#0369A1] font-outfit text-[16px] font-medium mb-2">
                {child.number} {child.title}
              </h3>
              <div className="text-[#374151] font-outfit text-[14px] leading-relaxed whitespace-pre-wrap">
                {child.response?.trim()
                  ? child.response
                  : "The requested information is not available in the knowledge base."}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  </div>
));
ReviewerView.displayName = "ReviewerView";

const DownloadDropdown = ({ onDownload, isDownloading, onClose }) => (
  <div
    className="absolute right-6 top-[52px] z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-2 w-36"
    onMouseEnter={() => {}}
    onMouseLeave={onClose}
  >
    {["pdf", "docx"].map((type) => (
      <button
        key={type}
        onClick={() => onDownload(type)}
        disabled={isDownloading}
        className="w-full px-4 py-2 text-left font-outfit text-[15px] text-gray-700 hover:bg-purple-50 hover:text-[#C95EBE] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        As {type.toUpperCase()}
      </button>
    ))}
  </div>
);

const LoadingOverlay = ({ message }) => (
  <div className="absolute inset-0 bg-white/95 backdrop-blur-sm z-20 flex items-center justify-center rounded-xl">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-[#C95EBE] mx-auto mb-4" />
      <p className="text-gray-600 font-outfit text-sm font-medium mb-1">
        {message}
      </p>
    </div>
  </div>
);

const PageLoader = () => (
  <div className="flex-1 flex items-center justify-center min-h-[60vh] w-full bg-[#FEF9F7]">
    <div className="text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#C95EBE] mx-auto mb-4" />
      <p className="text-gray-600 font-outfit text-lg">
        Loading proposal analysis...
      </p>
    </div>
  </div>
);

const ProposalAnalysisPage = () => {
  const { projectId, orgId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const editorRef = useRef(null);
  const downloadButtonRef = useRef(null);
  const cleanContentRef = useRef("");
  const saveTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  const [proposalData, setProposalData] = useState(null);
  const [projectName, setProjectName] = useState("");
  const [folderId, setFolderId] = useState(null);
  const [userPermission, setUserPermission] = useState(null);
  const [activeTab, setActiveTab] = useState("ai-analysis");
  const [chatMessages, setChatMessages] = useState([]);

  const [initialContent, setInitialContent] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // FIX: Added editorKey to forcefully reset TipTap's internal history stack on save
  const [editorKey, setEditorKey] = useState(0); 

  const [status, setStatus] = useState({
    page: "loading",
    editor: "loading",
    save: "idle",
    download: false,
    dropdownOpen: false,
  });

  const [analysisData, setAnalysisData] = useState({
    summary: "",
    strengths: [],
    deviations: [],
    recommendations: [],
  });
  const [complianceScore, setComplianceScore] = useState(0);

  const updateStatus = useCallback((patch) => {
    setStatus((prev) => ({ ...prev, ...patch }));
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedChanges]);

  const hydrateProposalData = useCallback((data, source = "api") => {
    setProposalData(data);

    if (data.user_permission) {
      setUserPermission(data.user_permission);
    }

    if (data.proposal_analysis) {
      const a = data.proposal_analysis;
      setComplianceScore(a.compliance_score || 0);
      setAnalysisData({
        summary: a.summary || "",
        strengths: parseListString(a.strengths),
        deviations: parseListString(a.deviations),
        recommendations: parseListString(a.recommendations),
      });
    }

    if (source === "api" && data.proposal_content) {
      const html = convertProposalContentToHTML(data.proposal_content);

      cleanContentRef.current = html;
      setInitialContent(html);
      setEditorKey((prev) => prev + 1); // FIX: Reset editor on hydrate
      setHasUnsavedChanges(false);
    }
  }, []);

  const fetchProposalFromAPI = useCallback(
    async (targetFolderId) => {
      try {
        const response = await api.post(
          `api/v1/rfp/folders/${targetFolderId}/generate-proposal/`,
          {}
        );

        if (!isMountedRef.current) return;

        if (response.data) {
          hydrateProposalData(response.data, "api");

          if (response.data.project_name || response.data.folder_name) {
            setProjectName(response.data.project_name || response.data.folder_name);
          }
          if (response.data.folder_id) {
            setFolderId(response.data.folder_id);
          }
        }
      } catch (error) {
        console.error("Error fetching proposal:", error);
        if (isMountedRef.current) {
          toast.error("Failed to load proposal. Please try again.");
        }
      }
    },
    [hydrateProposalData]
  );

  useEffect(() => {
    const {
      proposalData: navProposalData,
      projectName: navProjectName,
      folderId: navFolderId,
    } = location.state || {};

    if (navProposalData) {
      setProjectName(navProposalData.folder_name || navProposalData.project_name || navProjectName || "Untitled Project");
      setFolderId(navFolderId);
      hydrateProposalData(navProposalData, "router-state");
      updateStatus({ page: "ready", editor: "loading" });
    }

    if (projectId) {
      fetchProposalFromAPI(projectId).finally(() => {
        if (!isMountedRef.current) return;
        updateStatus({ page: "ready", editor: "ready" });
      });
    } else {
      updateStatus({ page: "ready" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleEditorReady = useCallback((editor) => {
    cleanContentRef.current = editor.getHTML();
    setStatus((prev) => ({ ...prev, editor: "ready" }));
  }, []);

  const handleEditorChange = useCallback((html) => {
    setHasUnsavedChanges(html !== cleanContentRef.current);
  }, []);

  const handleImageUpload = useCallback(async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(file);
    });
  }, []);

  const refreshProposalContent = useCallback(async () => {
    const targetFolderId = folderId || projectId;
    if (!targetFolderId) {
      toast.error("Folder ID not found");
      return;
    }

    updateStatus({ editor: "refreshing" });

    try {
      const response = await api.post(
        `api/v1/rfp/folders/${targetFolderId}/generate-proposal/`,
        {}
      );

      if (!isMountedRef.current) return;

      if (response.data?.proposal_content) {
        const updatedHtml = convertProposalContentToHTML(
          response.data.proposal_content
        );

        // FIX: Force remount by updating key, implicitly wiping history state
        cleanContentRef.current = updatedHtml;
        setInitialContent(updatedHtml);
        setEditorKey((prev) => prev + 1); 
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      console.error("Error refreshing proposal:", error);
      if (isMountedRef.current) toast.error("Failed to refresh proposal content");
    } finally {
      if (isMountedRef.current) updateStatus({ editor: "ready" });
    }
  }, [folderId, projectId, updateStatus]);

  const handleDownload = useCallback(
    async (type) => {
      const targetFolderId = folderId || projectId;
      if (!targetFolderId) {
        toast.error("Folder ID not found");
        return;
      }

      updateStatus({ download: true, dropdownOpen: false });

      try {
        let validProjectName = projectName;
        if (!validProjectName || validProjectName === "Go to My Projects") {
          validProjectName = proposalData?.project_name || proposalData?.folder_name || "Project";
        }
        const documentTitle = `Proposal for ${validProjectName}`;

        if (type === "pdf") {
          const currentContent = editorRef.current 
            ? editorRef.current.getContent() 
            : cleanContentRef.current;

          const pageStyles = Array.from(document.querySelectorAll('style'))
            .map((s) => s.outerHTML)
            .join('\n');

          const fullHtmlTemplate = `
            <!DOCTYPE html>
            <html>
              <head>
                <meta charset="utf-8">
                <title>${documentTitle}</title>
                <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700&display=swap" rel="stylesheet">
                ${pageStyles}
                <style>
                  body { padding: 40px; background: white; margin: 0; font-family: 'Outfit', sans-serif; }
                  .toolbar-wrapper { display: none !important; }
                  .action-bar { position: fixed; top: 24px; right: 24px; display: flex; gap: 12px; z-index: 9999; }
                  .action-btn { background-color: #C95EBE; color: white; border: none; padding: 10px 20px; border-radius: 6px; font-family: 'Outfit', sans-serif; font-weight: 500; font-size: 15px; cursor: pointer; }
                  .action-btn.secondary { background-color: white; color: #374151; border: 1px solid #D1D5DB; }
                  @media print { .no-print { display: none !important; } body { padding: 0; } }
                </style>
              </head>
              <body>
                <div class="action-bar no-print">
                  <button class="action-btn secondary" onclick="window.close()">Close</button>
                  <button class="action-btn" onclick="window.print()">Save as PDF</button>
                </div>
                <div class="proposal-editor mt-12">
                  <div class="ProseMirror">
                    ${currentContent}
                  </div>
                </div>
                <script>window.onload = function() { setTimeout(function() { window.print(); }, 500); };</script>
              </body>
            </html>
          `;

          const blob = new Blob([fullHtmlTemplate], { type: 'text/html;charset=utf-8' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.target = "_blank";
          link.rel = "noopener noreferrer"; 
          document.body.appendChild(link);
          link.click();
          link.remove();
          setTimeout(() => window.URL.revokeObjectURL(url), 10000);
          toast.success("Opened print dialog to save as PDF!");

        } else {
          // Uses the backend API logic for DOCX exactly as provided
          const response = await api.get(
            `api/v1/rfp/folders/${targetFolderId}/generate-proposal/download/?type=${type}`,
            { responseType: "blob" }
          );

          let filename = `${documentTitle}.${type}`;
          const contentDisposition = response.headers?.["content-disposition"];
          if (contentDisposition) {
            const match = /filename\*?=(?:UTF-8''|")?([^;"']+)/i.exec(contentDisposition);
            if (match && match[1]) {
              filename = decodeURIComponent(match[1].replace(/"/g, ""));
            }
          }

          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", filename);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);

          toast.success(`${type.toUpperCase()} downloaded successfully!`);
        }
      } catch (error) {
        console.error("Download error:", error);
        toast.error(`Failed to download ${type.toUpperCase()}`);
      } finally {
        if (isMountedRef.current) updateStatus({ download: false });
      }
    },
    [folderId, projectId, projectName, proposalData, updateStatus]
  );

  const handleSave = useCallback(async () => {
    if (!editorRef.current) return;

    try {
      updateStatus({ save: "saving" });

      const currentHtml = editorRef.current.getContent();
      const updatedContent = parseHTMLToStructuredContent(currentHtml);

      await api.patch(`/api/v1/rfp/folders/${projectId}/generate-proposal/`, {
        folder_id: parseInt(projectId, 10),
        proposal_content: updatedContent,
      });

      if (!isMountedRef.current) return;

      // FIX: Lock in the saved HTML, clear dirty flags, and forcefully remount to wipe history
      cleanContentRef.current = currentHtml;
      setInitialContent(currentHtml);
      setEditorKey((prev) => prev + 1);
      setHasUnsavedChanges(false);
      updateStatus({ save: "saved" });
      toast.success("Changes saved successfully!");

      await refreshProposalContent();

      saveTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current) updateStatus({ save: "idle" });
      }, 500);
    } catch (error) {
      console.error("Save error:", error);
      if (isMountedRef.current) {
        toast.error("Failed to save changes");
        updateStatus({ save: "idle" });
      }
    }
  }, [projectId, refreshProposalContent, updateStatus]);

  const buildAnalysisPath = useCallback(
    (suffix) =>
      orgId
        ? `/rfp-projects/project/${projectId}/org/${orgId}/proposal/analysis/${suffix}`
        : `/rfp-projects/project/${projectId}/proposal/analysis/${suffix}`,
    [orgId, projectId]
  );

  const handleSwitchView = useCallback(() => {
    navigate(buildAnalysisPath("section-view"), {
      state: { proposalData, projectName, folderId, orgId },
    });
  }, [buildAnalysisPath, folderId, navigate, orgId, projectName, proposalData]);

  const showSaveButton = useMemo(
    () => hasUnsavedChanges || status.save !== "idle",
    [hasUnsavedChanges, status.save]
  );

  const editorOverlayMessage = useMemo(() => {
    if (status.editor === "refreshing") return "Updating proposal content...";
    if (status.editor === "loading") return "Loading editor content...";
    return null;
  }, [status.editor]);

  if (status.page === "loading") return <PageLoader />;

  return (
    <div className="h-full w-full bg-[#FEF9F7] flex flex-col overflow-hidden relative">
      <Header
        icon="/arrow-back.svg"
        name={projectName || proposalData?.folder_name || proposalData?.project_name || "Project Proposal"}
        onSwitchView={handleSwitchView}
        onDownload={() =>
          updateStatus({ dropdownOpen: !status.dropdownOpen })
        }
        isDownloading={status.download}
        isSwitchingView={false}
        isProposalAnalysisPage
        showDownloadDropdown={status.dropdownOpen}
        downloadButtonRef={downloadButtonRef}
        projectId={projectId}
        orgId={orgId}
        showSaveButton={showSaveButton}
        onSave={handleSave}
        saveStatus={status.save}
      />

      {status.dropdownOpen && (
        <DownloadDropdown
          onDownload={handleDownload}
          isDownloading={status.download}
          onClose={() => updateStatus({ dropdownOpen: false })}
        />
      )}

      <div className="px-4 mt-4 mb-6 flex-1 flex gap-4 overflow-hidden min-h-0 w-full box-border">
        <div className="w-[30%] min-w-0 flex flex-col bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="py-4 px-2 pb-0">
            <div className="flex bg-[#F7F7F9] rounded-full border border-gray-200 p-1">
              {[
                { key: "ai-analysis", label: "AI Analysis" },
                { key: "chat", label: "Chat with Proposal" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex-1 px-2 py-2 rounded-full font-outfit font-medium text-[15px] transition-all ${
                    activeTab === tab.key
                      ? "text-white bg-[#C95EBE] shadow-sm"
                      : "text-[#C95EBE] hover:bg-purple-50"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 custom-scrollbar min-h-0">
            {activeTab === "ai-analysis" ? (
              <AIAnalysis
                analysisData={analysisData}
                complianceScore={complianceScore}
                userPermission={userPermission}
              />
            ) : (
              activeTab === "chat" && (
                <ChatWithProposal
                  proposalData={proposalData}
                  projectName={projectName}
                  messages={chatMessages}
                  setMessages={setChatMessages}
                  onRefreshProposal={refreshProposalContent}
                  isActive={activeTab === "chat"}
                />
              )
            )}
          </div>
        </div>

        <div className="w-[70%] min-w-0 flex flex-col bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden relative">
          {userPermission === "reviewer" ? (
            <ReviewerView proposalContent={proposalData?.proposal_content} />
          ) : (
            <>
              {(editorOverlayMessage || !initialContent) && (
                <LoadingOverlay
                  message={editorOverlayMessage || "Loading editor content..."}
                />
              )}
              <div className="flex-1 overflow-hidden relative min-h-0">
                {initialContent ? (
                  <ProposalEditor
                    key={editorKey} // FIX: Forces TipTap to unmount and remount with clean history state
                    ref={editorRef}
                    initialContent={initialContent}
                    onChange={handleEditorChange}
                    onReady={handleEditorReady}
                    onImageUpload={handleImageUpload}
                  />
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProposalAnalysisPage;